<template>
  <div>
    <!-- PC 레이아웃 (768px 이상) -->
    <div class="desktop-layout" v-show="!isMobile">
      <div class="flex flex-wrap items-center my-4 gap-4">
        <h5><i class="dot"></i> {{ $t(title) }}</h5>
        <div v-if="!isPrint && !readonly" class="ml-auto">
          <vs-button class="" color="secondary" type="border" @click="handleAddRow">{{$t('항목추가')}} </vs-button>
          <vs-button class="ml-4" color="secondary" type="border" :disabled="checkIds.length === 0" @click="handleRemoveRow">{{$t('선택삭제')}} </vs-button>
        </div>
      </div>
      <div class="overflow-x-auto">
        <table :style="{ minWidth: isPrint ? '' : '1400px' }">
          <colgroup>
            <col v-if="!isPrint && !readonly" width="36" style="background: #f9f9f9" />
            <col width="160" />
            <col />
            <col width="130" />
            <col width="130" />
            <col width="130" />
            <col width="160" />
            <col width="260" />
          </colgroup>
          <thead>
            <tr>
              <th v-if="!isPrint && !readonly" rowspan="2" class="p-0"><vs-checkbox :value="0 < list.length && checkIds.length === list.length" @click.native="onClickAllCheck" class="checkbox" /></th>
              <th rowspan="2">{{$t('세부작업명')}}</th>
              <th rowspan="2">{{$t('검토의견')}}</th>
              <th colspan="3">{{$t('검토의견 반영 위험도')}}</th>
              <th rowspan="2">{{$t('검토자')}}</th>
              <th rowspan="2">{{$t('비고')}}</th>
            </tr>
            <tr>
              <th>
                <div>
                  <p>{{$t('가능성')}}</p>
                  <vx-tooltip :title="$t('가능성이란?')" :html="$t('자세한 정보를 보시려면<br>느낌표ⓘ 아이콘을 클릭하세요')" style="margin-left: 4px; display: flex; align-items: center; justify-content: center" position="top" delay=".1s">
                    <feather-icon icon="AlertCircleIcon" style="cursor: pointer; width: 16px" @click="onClickToolTip1()" />
                  </vx-tooltip>
                </div>
              </th>
              <th>
                <div>
                  <p>{{$t('중대성')}}</p>
                  <vx-tooltip :title="$t('중대성이란?')" :html="$t('자세한 정보를 보시려면<br>느낌표ⓘ 아이콘을 클릭하세요')" style="margin-left: 4px; display: flex; align-items: center; justify-content: center" position="top" delay=".1s">
                    <feather-icon icon="AlertCircleIcon" style="cursor: pointer; width: 16px" @click="onClickToolTip2()" />
                  </vx-tooltip>
                </div>
              </th>
              <th>
                <div>
                  <p>{{$t('위험도')}}</p>
                  <vx-tooltip :title="$t('위험도이란?')" :html="$t('자세한 정보를 보시려면<br>느낌표ⓘ 아이콘을 클릭하세요')" style="margin-left: 4px; display: flex; align-items: center; justify-content: center" position="top" delay=".1s">
                    <feather-icon icon="AlertCircleIcon" style="cursor: pointer; width: 16px" @click="onClickToolTip3()" />
                  </vx-tooltip>
                </div>
              </th>
            </tr>
          </thead>
          <tbody>
            <tr v-if="list.length === 0">
              <td :colspan="isPrint || readonly ? 7 : 8" class="text-center" style="padding: 40px 20px; color: #999; background-color: #fafafa">
                <div class="center-flex">
                  <feather-icon icon="InfoIcon" class="mr-2" />
                  {{$t('s항목이 존재하지 않습니다.', { s: title })}}
                </div>
              </td>
            </tr>
            <template v-else>
              <tr v-for="(item, item_i) in list" :key="item.id">
                <td v-if="!isPrint && !readonly" class="p-0"><vs-checkbox :vs-value="item.id" v-model="checkIds" class="checkbox" /></td>
                <td>
                  <p v-if="isPrint" class="print-textarea no-block">{{ item.input1 }}</p>
                  <ml-textarea v-else :readonly="readonly" rows="3" v-model="item.input1" class="w-full mb-0" />
                </td>
                <td>
                  <p v-if="isPrint" class="print-textarea no-block">{{ item.input2 }}</p>
                  <ml-textarea v-else :readonly="readonly" rows="3" v-model="item.input2" class="w-full mb-0" />
                </td>
                <td>
                  <p v-if="isPrint" class="text-center">{{ item.input3 }}</p>
                  <CustomOptionSelector v-else :readonly="readonly" :options="[1, 2, 3, 4, 5]" v-model="item.input3" @input="changeRisk(item_i)" class="w-full" />
                </td>
                <td>
                  <p v-if="isPrint" class="text-center">{{ item.input4 }}</p>
                  <CustomOptionSelector v-else :readonly="readonly" :options="[1, 2, 3, 4]" v-model="item.input4" @input="changeRisk(item_i)" class="w-full" />
                </td>
                <td>
                  <p v-if="isPrint" class="text-center" :style="{ color: riskColor(item.input5) }">{{ item.input5 }}</p>
                  <ml-input v-else readonly :value="$t(item.input5)" class="w-full text-center" :style="{ color: riskColor(item.input5) }" />
                </td>
                <td>
                  <p v-if="isPrint">{{ item.input6 }}</p>
                  <ml-input v-else :readonly="readonly" v-model="item.input6" class="w-full" />
                </td>
                <td>
                  <p v-if="isPrint" class="print-textarea no-block">{{ item.input7 }}</p>
                  <ml-textarea v-else :readonly="readonly" v-model="item.input7" rows="3" class="w-full mb-0" />
                </td>
              </tr>
            </template>
          </tbody>
        </table>
      </div>
    </div>

    <!-- 모바일 레이아웃 (768px 미만) -->
    <div class="mobile-layout" v-show="isMobile">
      <!-- 모바일 헤더 -->
      <div class="mobile-header">
        <h5 class="mobile-title"><i class="dot"></i> {{ $t(title) }}</h5>

        <!-- 전체 선택 영역 -->
        <div v-if="!isPrint && !readonly && list.length > 0" class="mobile-select-all">
          <vs-checkbox :value="0 < list.length && checkIds.length === list.length" @click.native="onClickAllCheck" class="mobile-checkbox" />
          <span class="select-all-text">{{$t('전체 선택')}} ({{ checkIds.length }}/{{ list.length }})</span>
        </div>
      </div>

      <!-- 빈 목록 표시 -->
      <div v-if="list.length === 0" class="mobile-empty-card">
        <feather-icon icon="InfoIcon" />
        <p class="empty-text">{{$t('s항목이 존재하지 않습니다.', { s: title })}}</p>
      </div>

      <!-- 모바일 카드 목록 -->
      <div v-else class="mobile-cards-container">
        <div v-for="(item, item_i) in list" :key="item.id" class="mobile-card">
          <!-- 카드 헤더 -->
          <div class="mobile-card-header">
            <vs-checkbox v-if="!isPrint && !readonly" :vs-value="item.id" v-model="checkIds" class="mobile-checkbox" />
            <span class="card-number">{{$t('항목')}} {{ item_i + 1 }}</span>
            <vs-button v-if="!isPrint && !readonly" color="danger" type="border" size="small" @click="handleRemoveSingleRow(item.id)" class="mobile-delete-btn">{{$t('삭제')}}  </vs-button>
          </div>

          <!-- 세부작업명 -->
          <div class="mobile-field">
            <label class="mobile-label">{{$t('세부작업명')}}</label>
            <div class="mobile-input-container">
              <p v-if="isPrint" class="mobile-print-text">{{ item.input1 }}</p>
              <ml-textarea v-else :readonly="readonly" rows="3" v-model="item.input1" class="mobile-textarea" />
            </div>
          </div>

          <!-- 검토의견 -->
          <div class="mobile-field">
            <label class="mobile-label">{{$t('검토의견')}}</label>
            <div class="mobile-input-container">
              <p v-if="isPrint" class="mobile-print-text">{{ item.input2 }}</p>
              <ml-textarea v-else :readonly="readonly" rows="3" v-model="item.input2" class="mobile-textarea" />
            </div>
          </div>

          <!-- 위험도 분석 -->
          <div class="mobile-risk-analysis">
            <div class="mobile-risk-header">
              <span class="risk-title">{{$t('검토의견 반영 위험도')}}</span>
            </div>
            <div class="mobile-risk-grid">
              <div class="mobile-risk-item">
                <div class="risk-item-header">
                  <span class="risk-label">{{$t('가능성')}}</span>
                  <feather-icon icon="AlertCircleIcon" class="risk-info-icon" @click="onClickToolTip1()" />
                </div>
                <div class="risk-value-container">
                  <p v-if="isPrint" class="risk-print-value">{{ item.input3 }}</p>
                  <CustomOptionSelector v-else :readonly="readonly" :options="[1, 2, 3, 4, 5]" v-model="item.input3" @input="changeRisk(item_i)" class="mobile-selector" />
                </div>
              </div>

              <div class="mobile-risk-item">
                <div class="risk-item-header">
                  <span class="risk-label">{{$t('중대성')}}</span>
                  <feather-icon icon="AlertCircleIcon" class="risk-info-icon" @click="onClickToolTip2()" />
                </div>
                <div class="risk-value-container">
                  <p v-if="isPrint" class="risk-print-value">{{ item.input4 }}</p>
                  <CustomOptionSelector v-else :readonly="readonly" :options="[1, 2, 3, 4]" v-model="item.input4" @input="changeRisk(item_i)" class="mobile-selector" />
                </div>
              </div>

              <div class="mobile-risk-item highlight">
                <div class="risk-item-header">
                  <span class="risk-label">{{$t('위험도')}}</span>
                  <feather-icon icon="AlertCircleIcon" class="risk-info-icon" @click="onClickToolTip3()" />
                </div>
                <div class="risk-value-container">
                  <p v-if="isPrint" class="risk-print-value" :style="{ color: riskColor(item.input5) }">{{ item.input5 }}</p>
                  <ml-input v-else readonly :value="$t(item.input5)" class="mobile-risk-input" :style="{ color: riskColor(item.input5) }" />
                </div>
              </div>
            </div>
          </div>

          <!-- 검토자 -->
          <div class="mobile-field">
            <label class="mobile-label">{{$t('검토자')}}</label>
            <div class="mobile-input-container">
              <p v-if="isPrint" class="mobile-print-text">{{ item.input6 }}</p>
              <ml-input v-else :readonly="readonly" v-model="item.input6" class="mobile-input" />
            </div>
          </div>

          <!-- 비고 -->
          <div class="mobile-field">
            <label class="mobile-label">{{$t('비고')}}</label>
            <div class="mobile-input-container">
              <p v-if="isPrint" class="mobile-print-text">{{ item.input7 }}</p>
              <ml-textarea v-else :readonly="readonly" v-model="item.input7" rows="3" class="mobile-textarea" />
            </div>
          </div>
        </div>
      </div>

      <!-- 모바일 하단 버튼 -->
      <div v-if="!isPrint && !readonly" class="mobile-bottom-actions">
        <vs-button color="danger" type="border" :disabled="checkIds.length === 0" @click="handleRemoveRow" class="mobile-action-btn"> {{$t('선택삭제')}} ({{ checkIds.length }}) </vs-button>
        <vs-button color="primary" @click="handleAddRow" class="mobile-action-btn mobile-add-btn"> {{$t('항목추가')}} </vs-button>
      </div>
    </div>

    <popup-risk-info_5x4_1 v-model="showInfoPopup1"></popup-risk-info_5x4_1>
    <popup-risk-info_5x4_2 v-model="showInfoPopup2"></popup-risk-info_5x4_2>
    <popup-risk-info_5x4_3 v-model="showInfoPopup3"></popup-risk-info_5x4_3>
  </div>
</template>

<script>
import CustomOptionSelector from "@/components/selector/CustomOptionSelector.vue";
import PopupRiskInfo_5x4_1 from "@/components/TableRiskAppraisal_5x4/PopupRiskInfo_5x4_1.vue";
import PopupRiskInfo_5x4_2 from "@/components/TableRiskAppraisal_5x4/PopupRiskInfo_5x4_2.vue";
import PopupRiskInfo_5x4_3 from "@/components/TableRiskAppraisal_5x4/PopupRiskInfo_5x4_3.vue";

export default {
  components: {
    PopupRiskInfo_5x4_3,
    PopupRiskInfo_5x4_2,
    PopupRiskInfo_5x4_1,
    CustomOptionSelector,
  },
  props: {
    title: { type: String, default: "" },
    value: { type: Array, default: () => [] },
    readonly: { type: Boolean, default: false },
    isPrint: { type: Boolean, default: false },
  },
  data() {
    return {
      checkIds: [],
      list: this.value,
      numberId: 0 < this.value.length ? this.value[this.value.length - 1].id : 0,
      windowWidth: window.innerWidth,

      showInfoPopup1: false,
      showInfoPopup2: false,
      showInfoPopup3: false,
    };
  },
  computed: {
    isMobile() {
      return this.windowWidth < 768;
    },
  },
  mounted() {
    // 화면 크기 변경 감지
    this.handleResize = () => {
      this.windowWidth = window.innerWidth;
    };
    window.addEventListener("resize", this.handleResize);
  },
  beforeDestroy() {
    window.removeEventListener("resize", this.handleResize);
  },
  watch: {
    value: {
      handler(value) {
        this.list = value;
        this.numberId = 0 < value.length ? value[value.length - 1].id : 0;
      },
    },
    list: {
      immediate: true,
      deep: true,
      handler(value) {
        this.$emit("input", value);
      },
    },
  },
  methods: {
    handleAddRow() {
      this.list.push({ id: ++this.numberId, input3: 1, input4: 1, input5: "매우낮음" });
    },
    handleRemoveRow() {
      this.list = this.list.filter((item) => !this.checkIds.includes(item.id));
      this.checkIds = [];
    },
    handleRemoveSingleRow(id) {
      this.list = this.list.filter((item) => item.id !== id);
      this.checkIds = this.checkIds.filter((checkId) => checkId !== id);
    },
    onClickAllCheck() {
      if (this.checkIds.length === this.list.length) this.checkIds = [];
      else this.checkIds = this.list.map((item) => item.id);
    },
    changeRisk(index) {
      const risk = this.list[index].input3 * this.list[index].input4;
      let riskKo = "";
      if (risk <= 3) riskKo = "매우낮음";
      else if (risk <= 6) riskKo = "낮음";
      else if (risk === 8) riskKo = "보통";
      else if (risk <= 12) riskKo = "약간높음";
      else if (risk === 15) riskKo = "높음";
      else if (risk <= 20) riskKo = "매우높음";
      this.list[index].input5 = riskKo;
      this.$set(this.list[index], "input5", riskKo);
    },
    riskColor(risk) {
      switch (risk) {
        case "매우낮음":
          return "#28C76F";
        case "낮음":
          return "#FF8510";
        default:
          return "#E42728";
      }
    },
    onClickToolTip1() {
      this.showInfoPopup1 = true;
    },
    onClickToolTip2() {
      this.showInfoPopup2 = true;
    },
    onClickToolTip3() {
      this.showInfoPopup3 = true;
    },
  },
};
</script>

<style lang="scss" scoped>
.center-flex {
  display: flex;
  justify-content: center;
  align-items: center;
}

.checkbox {
  justify-content: center !important;
  &::v-deep {
    span {
      margin: 0px;
    }
  }
}
table {
  width: 100%;
  table-layout: fixed;
  border-collapse: collapse;

  tr {
    th {
      font-weight: 700;
      font-size: 14px;
      line-height: 18px;
      align-items: center;
      text-align: center;

      background: #f9f9f9;
      border: 1px solid #cdcdcd;

      padding: 16px 6px;

      & > div {
        display: flex;
        align-items: center;
        justify-content: center;
      }
    }

    td {
      vertical-align: top;
      border: 1px solid #cdcdcd;
      padding: 6px;
      &:first-child {
        vertical-align: middle;
      }
    }
  }
}

// 모바일 레이아웃 스타일
@media (max-width: 767px) {
  .desktop-layout {
    display: none !important;
  }
}

@media (min-width: 768px) {
  .mobile-layout {
    display: none !important;
  }
}

.mobile-layout {
  .mobile-header {
    margin-bottom: 16px;

    .mobile-title {
      font-size: 16px;
      font-weight: 600;
      margin-bottom: 12px;
      color: #333;

      .dot {
        display: inline-block;
        width: 8px;
        height: 8px;
        background: #007bff;
        border-radius: 50%;
        margin-right: 8px;
      }
    }

    .mobile-select-all {
      display: flex;
      align-items: center;
      padding: 12px 16px;
      background: #f8f9fa;
      border-radius: 8px;
      border: 1px solid #e9ecef;

      .mobile-checkbox {
        margin-right: 12px;
        justify-content: center !important;

        &::v-deep span {
          margin: 0;
        }
      }

      .select-all-text {
        font-size: 14px;
        color: #666;
        font-weight: 500;
      }
    }
  }

  .mobile-empty-card {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 40px 20px;
    background: #fafafa;
    border: 1px solid #e9ecef;
    border-radius: 12px;
    text-align: center;

    .empty-icon {
      width: 48px;
      height: 48px;
      color: #999;
      margin-bottom: 16px;
    }

    .empty-text {
      color: #999;
      font-size: 16px;
      margin: 0;
    }
  }

  .mobile-cards-container {
    .mobile-card {
      background: white;
      border: 1px solid #e9ecef;
      border-radius: 12px;
      padding: 16px;
      margin-bottom: 16px;
      box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);

      .mobile-card-header {
        display: flex;
        align-items: center;
        justify-content: space-between;
        margin-bottom: 16px;
        padding-bottom: 12px;
        border-bottom: 1px solid #e9ecef;

        .mobile-checkbox {
          margin-right: 12px;
          justify-content: center !important;

          &::v-deep span {
            margin: 0;
          }
        }

        .card-number {
          flex: 1;
          font-size: 14px;
          font-weight: 600;
          color: #333;
        }

        .mobile-delete-btn {
          min-height: 32px;
          font-size: 12px;
          padding: 6px 12px;
        }
      }

      .mobile-field {
        margin-bottom: 16px;

        .mobile-label {
          display: block;
          font-size: 13px;
          font-weight: 600;
          color: #333;
          margin-bottom: 6px;
        }

        .mobile-input-container {
          .mobile-print-text {
            font-size: 14px;
            color: #555;
            line-height: 1.5;
            margin: 0;
            word-break: break-word;
          }

          .mobile-textarea,
          .mobile-input {
            width: 100%;
            margin-bottom: 0;
          }
        }
      }

      .mobile-risk-analysis {
        background: #f8f9fa;
        border-radius: 8px;
        padding: 16px;
        margin-bottom: 16px;

        .mobile-risk-header {
          margin-bottom: 12px;

          .risk-title {
            font-size: 14px;
            font-weight: 600;
            color: #333;
          }
        }

        .mobile-risk-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 12px;

          .mobile-risk-item {
            background: white;
            border: 1px solid #e9ecef;
            border-radius: 6px;
            padding: 12px;

            &.highlight {
              grid-column: 1 / -1;
              background: #fff8e1;
              border-color: #ffb300;
            }

            .risk-item-header {
              display: flex;
              align-items: center;
              justify-content: space-between;
              margin-bottom: 8px;

              .risk-label {
                font-size: 12px;
                font-weight: 600;
                color: #333;
              }

              .risk-info-icon {
                width: 14px;
                height: 14px;
                color: #666;
                cursor: pointer;

                &:hover {
                  color: #007bff;
                }
              }
            }

            .risk-value-container {
              .risk-print-value {
                font-size: 16px;
                font-weight: 600;
                text-align: center;
                margin: 0;
              }

              .mobile-selector,
              .mobile-risk-input {
                width: 100%;
                text-align: center;
                font-weight: 600;
              }
            }
          }
        }
      }
    }
  }

  .mobile-bottom-actions {
    display: flex;
    gap: 12px;
    padding: 16px;
    background: #f8f9fa;
    border-radius: 8px;
    margin-top: 16px;

    .mobile-action-btn {
      flex: 1;
      min-height: 44px;
      font-size: 14px;
      font-weight: 600;

      &.mobile-add-btn {
        background: linear-gradient(135deg, #007bff 0%, #0056b3 100%);
        color: white;
        border: none;
      }
    }
  }
}
</style>
